/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createOrganizationProject } from '../../api';
import {
  appProductDescriptions,
  appProductLabels,
  appProductOrder,
  normalizeAppProducts,
  primaryAppProduct,
} from '../../app-products';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type ProjectDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function ProjectDialog({ actionDialogReturn, onNavigate: _onNavigate }: ProjectDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const { activeOrganization, setActiveProjectId, setWorkspaceVersion } = useWorkspace();

  if (!actionDialog || actionDialog.kind !== 'project') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'project' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      if (activeOrganization) {
        const selectedProducts = normalizeAppProducts(actionDialog.enabledProducts);
        const selectedPrimaryProduct = primaryAppProduct(selectedProducts);
        const app = await createOrganizationProject(
          session.accessToken,
          activeOrganization.id,
          {
            name: actionDialog.name.trim(),
            kind:
              selectedPrimaryProduct === 'ota'
                ? 'release'
                : selectedPrimaryProduct === 'notifications'
                  ? 'notification'
                  : 'product',
            enabledProducts: selectedProducts
          }
        );
        setActiveProjectId(app.tenantAppId);
        setWorkspaceVersion((v) => v + 1);
      }
      toast('success', 'Project created');
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Project</div>
            <h3>New project</h3>
            <p>Choose which product capabilities this project should enable.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Name</span>
            <input
              value={actionDialog.name}
              onChange={(e) => setActionDialog({ ...actionDialog, name: e.target.value })}
              placeholder="Acme Platform"
            />
          </label>
          <p className="fieldHint">The system will turn this into a unique project slug behind the scenes.</p>
          <div className="settingsField">
            <span>Enabled products</span>
            <div className="onboardingChoiceGrid">
              {appProductOrder.map((product) => {
                const active = actionDialog.enabledProducts.includes(product);
                return (
                  <button
                    key={product}
                    type="button"
                    className={`onboardingChoice${active ? ' active' : ''}`}
                    onClick={() =>
                      setActionDialog((current) =>
                        current && current.kind === 'project'
                          ? {
                              ...current,
                              enabledProducts: current.enabledProducts.includes(product)
                                ? current.enabledProducts.filter((v) => v !== product)
                                : [...current.enabledProducts, product],
                              appKind: current.enabledProducts.includes(product)
                                ? current.appKind
                                : product === 'notifications'
                                  ? 'notification'
                                  : product === 'workspace'
                                    ? 'product'
                                    : 'release'
                            }
                          : current
                      )
                    }
                  >
                    <strong>{appProductLabels[product]}</strong>
                    <span>{appProductDescriptions[product]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy}>
            {dialogBusy ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { ProjectDialog };
